import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Doesn't match practice", "Correction", "Unclear / confusing", "Missing content", "Risk concern", "Other"];
const OUTCOMES = ["ACCEPTED", "REJECTED", "DEFERRED"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

function ResolveControls({ fb, onSaved }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [resolution, setResolution] = useState("");
  const save = async () => {
    try {
      const res = await axios.put(`${API}/feedback/${fb.id}/resolve`, { outcome, resolution });
      onSaved(res.data);
      toast.success(`${fb.fb_number} resolved — ${outcome}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`fb-resolve-${fb.fb_number}`}>Resolve…</button>;
  return (
    <div className="vr-form">
      <Select value={outcome} onValueChange={setOutcome}>
        <SelectTrigger className="inp sel" data-testid={`fb-outcome-${fb.fb_number}`}><SelectValue placeholder="Outcome…" /></SelectTrigger>
        <SelectContent>{OUTCOMES.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
      </Select>
      <input className="inp" placeholder="Resolution note (what changed, or why not)" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid={`fb-resolution-${fb.fb_number}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={save} disabled={!outcome || !resolution.trim()} data-testid={`fb-resolve-save-${fb.fb_number}`}>Save</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function FeedbackRegister({ active }) {
  const [rows, setRows] = useState(null);
  const [chapterRef, setChapterRef] = useState("");
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [proposed, setProposed] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/feedback`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "fb") fetchRows(); }, [active, fetchRows]);

  const canSubmit = chapterRef.trim() && type && description.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/feedback`, { chapter_ref: chapterRef, feedback_type: type, description, proposed_change: proposed });
      toast.success(`${res.data.fb_number} filed — thank you, it stays open until answered`);
      setChapterRef(""); setType(""); setDescription(""); setProposed("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not file the change request");
    }
    setSubmitting(false);
  };

  const onSaved = (d) => setRows((p) => p.map((x) => (x.id === d.id ? d : x)));
  const openCount = (rows || []).filter((r) => r.status === "OPEN").length;
  const pill = (o) => (o === "ACCEPTED" ? "cleared" : o === "REJECTED" ? "declined" : "open");

  return (
    <section className={`chapter utility${active === "fb" ? " active" : ""}`} id="fb">
      <div className="eyebrow">Working record · the manual review loop — if it's wrong, unclear, or missing, file it here and it gets an answer</div>
      <h1>Change Requests</h1>
      <p className="lede">Reviewing the manual? Anything that doesn't match how we actually work, reads unclear, or is flat-out missing gets filed here — stamped with your account. Every request stays OPEN until it's resolved with a written outcome: accepted into the manual, rejected with a reason, or deferred.</p>

      <div className="formcard" data-testid="fb-form">
        <div className="formcard-title">File a change request</div>
        <div className="fld-grid">
          <div className="fld"><label>Chapter / SOP reference</label><input className="inp" value={chapterRef} onChange={(e) => setChapterRef(e.target.value)} placeholder="e.g. SOP-4.3, Chapter 6, Appendix A-4" data-testid="fb-chapter" /></div>
          <div className="fld"><label>Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="inp sel" data-testid="fb-type"><SelectValue placeholder="What kind of issue…" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>What's wrong / what's missing</label>
          <textarea className="inp ta" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Be blunt — what the manual says vs what actually happens" data-testid="fb-description" />
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>Proposed change (optional)</label>
          <input className="inp" value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder="How you'd word it / what the rule should be" data-testid="fb-proposed" />
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="fb-submit">File change request</button>
      </div>

      <h2>Register {rows ? `— ${openCount} open` : ""}</h2>
      <table data-testid="fb-table">
        <tbody>
          <tr><th>FB #</th><th>Reference</th><th>Type</th><th>Request</th><th>Filed by</th><th style={{ width: 250 }}>Status / outcome</th></tr>
          {rows === null ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={6}>No change requests yet — reviewers file them here.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td className="q">{r.fb_number}</td>
                <td className="q">{r.chapter_ref}</td>
                <td>{r.feedback_type}</td>
                <td>{r.description}{r.proposed_change ? <div className="cap-sub">Proposed: {r.proposed_change}</div> : null}</td>
                <td>{r.recorded_by ? <>{r.recorded_by.name}<div className="cap-sub">{fmt(r.created_at)}</div></> : fmt(r.created_at)}</td>
                <td>
                  {r.status === "RESOLVED" ? (
                    <>
                      <span className={`vr-pill ${pill(r.outcome)}`} data-testid={`fb-status-${r.fb_number}`}>{r.outcome} {fmt(r.resolved_at)}</span>
                      <div className="cap-sub">{r.resolution}{r.resolved_by ? ` — ${r.resolved_by.name}` : ""}</div>
                    </>
                  ) : (
                    <>
                      <span className="vr-pill open" data-testid={`fb-status-${r.fb_number}`}>OPEN</span>
                      <ResolveControls fb={r} onSaved={onSaved} />
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Accepted requests drive the next manual version (the version log in Chapter 1); rejected ones keep their reasoning on record. Requests appear in the Audit Trail and export with the binder.</p>
    </section>
  );
}
